import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import {
  GlobalMessageService,
  GlobalMessageType,
  OccEndpointsService,
  Product,
  TranslationService
} from "@spartacus/core";
import { Observable } from 'rxjs';
import { take, tap } from "rxjs/operators";
import { CustomManufacturerCampaignDetailsComponent } from './components/custom-manufacturer-campaign-details/custom-manufacturer-campaign-details.component';
import { CustomManufacturerCampaignExportComponent } from './components/custom-manufacturer-campaign-export/custom-manufacturer-campaign-export.component';
import { ManufacturerCampaignInstructionsComponent } from './components/manufacturer-campaign-instructions/manufacturer-campaign-instructions.component';
import { CustomManufacturerCampaign, ManufacturerCampaignExportDataList, ManufacturerCampaignResultsData } from './custom-manufacturer-campaign.models';
import { AngularCsv } from 'angular-csv-ext';
import { DatePipe } from "@angular/common";

@Injectable({
  providedIn: 'root'
})
export class CustomManufacturerCampaignService {
  private importResponses = {
    "successfulEntries" : "customManufacturerCampaign.import.success",
    "unauthorizedCampaignCode":"customManufacturerCampaign.import.unauthorizedCampaignCode",
    "invalidEntries" : "customManufacturerCampaign.import.errors.invalidEntries",
    "hasDuplicates" : "customManufacturerCampaign.import.errors.hasDuplicates",
    "invalidFileType" : "customManufacturerCampaign.import.errors.invalidFileType"
  }

  constructor(
    private modalService: NgbModal,
    private httpClient: HttpClient,
    private endpoints: OccEndpointsService,
    private messageService: GlobalMessageService,
    private translation: TranslationService,
    private datePipe: DatePipe
  ) {}

  openInstructions() {
    this.modalService.open(ManufacturerCampaignInstructionsComponent, { centered: true, size: 'lg' });
  }

  importFile(file: File) {
    var formData: FormData = new FormData();
    formData.append("file", file, file.name);
    const url = this.endpoints.buildUrl(
      'customManufacturerCampaignSalesNotificationUpload'
    );
    this.httpClient
      .post(url, formData)
      .pipe(
        tap<ManufacturerCampaignResultsData[]>((data) => {
          this.showMessage(data);
        })
      )
      .subscribe((_res) => {});
  }

  showMessage(response: ManufacturerCampaignResultsData[]): void {
    const SHOW_MSG_TIMEOUT_INFO: number  = 600000; // 10 minutes;
    if (Object.keys(response).length > 0) {
      for (const msgResponse of response) {
        this.translation
          .translate(this.importResponses[msgResponse.message])
          .pipe(take(1))
          .subscribe((message) => {
            this.messageService.add(

              `${message} ${msgResponse.invalidItems ?  "<br/>" + msgResponse.invalidItems.join("<br/>") : msgResponse.ignoredCampaignCode ?"<br/>" + msgResponse.ignoredCampaignCode.join("<br/>"):""}`,
              msgResponse.message === 'successfulEntries'? GlobalMessageType.MSG_TYPE_INFO: msgResponse.message === 'unauthorizedCampaignCode'? GlobalMessageType.MSG_TYPE_WARNING: GlobalMessageType.MSG_TYPE_ERROR,
              SHOW_MSG_TIMEOUT_INFO
            );
          });
        }
      }
    }

  openDetails(
    expired: boolean,
    campaignName: string,
    campaignId: string,
    manufacturer: string,
    productCode: string,
    productName: string,
    productEan: string,
    supplierArticleNumber: string,
    noSerialNumberCheck: boolean,
    startedValidity: boolean,
    tradeInProduct: boolean,
    numberOfCombinations?: string,
    combiProducts?: Product[],
    combiValue1?: string,
    campaignCodeInput?:boolean) {
    let customSize = this.customSizeModal(tradeInProduct,numberOfCombinations);
    const modalRef = this.modalService.open(CustomManufacturerCampaignDetailsComponent, { centered: true, scrollable: true, size: customSize });
    modalRef.componentInstance.expired = expired;
    modalRef.componentInstance.campaignId = campaignId;
    modalRef.componentInstance.manufacturer = manufacturer;
    modalRef.componentInstance.productCode = productCode;
    modalRef.componentInstance.productName = productName;
    modalRef.componentInstance.productEan = productEan;
    modalRef.componentInstance.supplierArticleNumber = supplierArticleNumber;
    modalRef.componentInstance.campaignName = campaignName;
    modalRef.componentInstance.noSerialNumberCheck = noSerialNumberCheck;
    modalRef.componentInstance.startedValidity = startedValidity;
    modalRef.componentInstance.tradeInProduct = tradeInProduct;
    modalRef.componentInstance.numberOfCombinations = numberOfCombinations;
    modalRef.componentInstance.combiProducts = combiProducts;
    modalRef.componentInstance.combiValue1 = combiValue1;
    modalRef.componentInstance.campaignCodeInput = campaignCodeInput;
  }

  openExport(manufacturerCampaigns: CustomManufacturerCampaign[], b2bUnitId: string) {
    const modalRef = this.modalService.open(CustomManufacturerCampaignExportComponent, { centered: true, scrollable: true, size: 'md' });
    modalRef.componentInstance.manufacturerCampaigns = manufacturerCampaigns;
    modalRef.componentInstance.b2bUnitId = b2bUnitId;
  }

  exportManufacturerCampaigns(campaignDataList: ManufacturerCampaignExportDataList): Observable<Blob>{
    const url = this.endpoints.buildUrl('customManufacturerCampaignExport');
    return this.httpClient.post(url, campaignDataList, { observe: 'body', responseType: 'blob' });
  }

  downloadCampaignCalendar(campaignId: string): Observable<Blob> {
    const params = {
      campaignId
    };

    const url = this.endpoints.buildUrl('customManufacturerCampaignCalendarDownload', {queryParams: params});
    return this.httpClient.get(url , { observe: 'body', responseType: 'blob' });
  }

  customSizeModal(tradeInProduct?: boolean, numberOfCombinations?: string) {
    return (tradeInProduct || numberOfCombinations != 'ZERO') ? 'customSize' : 'lg';
  }

  downloadCsv(manufacturerCampaignList: CustomManufacturerCampaign[]) {
    const mappedData = manufacturerCampaignList.map(manufacturerCampaign => ({
      manufacturer: manufacturerCampaign?.manufacturer ?? '',
      id: this.getCampaignIdAndName(manufacturerCampaign),
      productCode: manufacturerCampaign?.productCode ?? '',
      ean: manufacturerCampaign?.productEan ?? '',
      name: manufacturerCampaign?.productName ?? '',
      value: this.calculateValue(manufacturerCampaign) ?? '',
      validFrom: manufacturerCampaign?.formattedValidFrom ?? '',
      validTo: manufacturerCampaign?.formattedValidTo ?? '',
      editableUntil: manufacturerCampaign?.formattedEditableUntil ?? '',
    }));

    const options = {
      headers: ['Hersteller', 'Aktion (ID - NAME)', 'Art.-Nr.', 'EAN', 'Produktinfo', 'Wert (€)', 'Zeitraum Von', 'Zeitraum Bis', 'Meldung Bis'],
    };

    new AngularCsv(mappedData, 'Aktionsliste_' + this.datePipe.transform(new Date(),'ddMMyyHHmmss', 'UTC'), options);
  }

  calculateValue(manufacturerCampaign: CustomManufacturerCampaign){
    if(manufacturerCampaign?.numberOfCombinations == 'ZERO'){
      return manufacturerCampaign?.formattedValue
    } else if (manufacturerCampaign?.numberOfCombinations == 'ONE' && manufacturerCampaign?.combiValue1){
      return manufacturerCampaign?.combiValue1;
    } else if(manufacturerCampaign?.numberOfCombinations == 'TWO'){
      return manufacturerCampaign?.combiValue1 + " " + manufacturerCampaign?.combiValue2;
    }
  }

  getCampaignIdAndName(campaign?: CustomManufacturerCampaign): string {
  if (!campaign) return '';

  return [
    campaign.id ?? '',
    campaign.numberOfCombinations !== 'ZERO' && campaign.id && campaign.name ? 'Kombi' : '',
    campaign.name ?? ''
  ]
  .filter(Boolean)
  .join(' - ');
}

}
